/**
 * Pure payload builders for analytics events. No React / MobX / store imports —
 * call sites pass plain values and spread the result into the EventTracker
 * payload. Keeping these here lets the call sites stay readable when an event
 * needs the same story/video/template context another event already carries.
 */

type StoryVideoContextInput =
  | {
      story?: { id?: string; title?: string } | null;
      currentVideo?: { id?: string; title?: string } | null;
    }
  | null
  | undefined;

export type StoryVideoContext = {
  story_id?: string;
  story_title?: string;
  video_id?: string;
  video_title?: string;
};

export function buildStoryVideoContext(
  videoCreator: StoryVideoContextInput,
): StoryVideoContext {
  return {
    story_id: videoCreator?.story?.id,
    story_title: videoCreator?.story?.title,
    video_id: videoCreator?.currentVideo?.id,
    video_title: videoCreator?.currentVideo?.title,
  };
}

export type ClipContext = {
  clip_id?: string;
  clip_title?: string;
};

export function buildClipContext(
  clipId?: string,
  clipTitle?: string,
): ClipContext {
  return { clip_id: clipId, clip_title: clipTitle };
}

export type TemplateChange = {
  template_id: string;
  template_name?: string;
  previous_template_id: string | null;
  previous_template_name: string | null;
};

export function buildTemplateChange(
  previous: { id?: string; name?: string },
  next: { id: string; name?: string },
): TemplateChange {
  return {
    template_id: next.id,
    template_name: next.name,
    previous_template_id: previous.id ?? null,
    previous_template_name: previous.name ?? null,
  };
}

export type SelectionTextStats = {
  selected_word_count: number;
  selected_text_preview: string;
};

const DEFAULT_PREVIEW_MAX_LEN = 100;

export function buildSelectionTextStats(
  text: string,
  maxPreviewLen: number = DEFAULT_PREVIEW_MAX_LEN,
): SelectionTextStats {
  return {
    selected_word_count: text.split(/\s+/).filter(Boolean).length,
    selected_text_preview: text.slice(0, maxPreviewLen),
  };
}
